import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { merge, pick, set, sortBy } from 'lodash'
import { useDebounceFn, watchPausable } from '@vueuse/core'

import { AxiosResponse } from 'axios'
import { Repository, RepositoryCurrency, RepositoryTimeline } from '@types'
import { ApiListResponse } from '../utils/types'
import { AxiosError, HttpStatusCode } from 'axios'

import {
  ACCESS_TYPE_OWNER,
  ACCESS_TYPE_READ_WRITE,
  ACCESS_TYPE_READONLY,
  DEFAULT_TIMELINE,
} from './utils/const'
import { DEBOUNCE_DELAY } from '@/const'

import {
  addStoreListItem,
  prepareResponseError,
  removeStoreListItem,
  updateStoreListItem,
} from '../utils/helpers'
import { getCurrentDate } from '@/helpers/dates'
import { getCurrencySymbol } from '@/helpers/common'
import runRepositorySettingsMigrations from './utils/helpers'

import api from '../api'

import useAnalyticsStore from '../analytics'
import { useAssetsStore } from '../assets'
import { useMainStore } from '../main'
import { useUserSettingsStore } from '../user/settings'

export const useRepositoriesStore = defineStore('repositories', () => {
  // INIT
  const analyticsStore = useAnalyticsStore()
  const assetsStore = useAssetsStore()
  const userSettingsStore = useUserSettingsStore()
  const mainStore = useMainStore()

  const list = ref<Repository[]>([])
  const currenciesList = ref<RepositoryCurrency[]>([])

  const initFlag = ref(false)
  const loading = ref(true)
  const loadingAction = ref(false)

  const internalTimelineEnd = ref<RepositoryTimeline['end']>(getCurrentDate({}))

  const internalUISettings =
    ref<Repository['user_repo_settings']['ui_settings']>()

  const treeSearch = ref<string>('')

  let abortController = new AbortController()

  // GETTERS

  const getList = computed(() => list.value)

  const getTreeSearch = computed(() => treeSearch.value)

  const getCurrencies = computed(() =>
    currenciesList.value.map(item => ({
      ...item,
      name: item.name ?? item.ticker,
    })),
  )

  const getCurrentId = computed(() =>
    list.value.length === 0
      ? undefined
      : userSettingsStore.getLastActiveRepository,
  )

  const getRepository = computed(() => {
    if (!list.value) return
    return list.value.find(item => item.id === getCurrentId.value)
  })

  const getCurrentName = computed(() => getRepository.value?.name)

  const getCurrentCurrency = computed(
    () =>
      getRepository.value?.currency || currenciesList.value?.[0]?.ticker || '',
  )

  const getCurrentCurrencySymbol = computed(
    () =>
      getCurrentCurrency.value && getCurrencySymbol(getCurrentCurrency.value),
  )

  const getCurrentSettings = computed(() => getRepository.value?.settings)

  const getCurrentRepoUserSettings = computed(
    () => getRepository.value?.user_repo_settings,
  )

  const getCurrentUISettings = computed(
    () => getCurrentRepoUserSettings.value?.ui_settings,
  )

  const getCurrentRepoUserAccessType = computed(
    () => getCurrentRepoUserSettings.value?.access_type,
  )

  const getCurrentTimeline = computed(
    () => ({
      ...pick(
        getCurrentRepoUserSettings.value,
        'periods',
        'freq',
        'end_is_today',
      ),
      end: internalTimelineEnd.value,
    } as RepositoryTimeline)
  )

  const getCurrentTimelineEnd = computed(() => getCurrentTimeline.value.end)

  const getCurrentTimelineFreq = computed(() => getCurrentTimeline.value.freq)

  const getCurrentTimelinePeriods = computed(() => getCurrentTimeline.value.periods || 0)

  const getCurrentCollaboratorsCount = computed(() => getRepository.value?.collaborators_count || 0)

  const getCurrentOwner = computed(() => getRepository.value?.owner)

  const isOwner = computed(() => {
    if (!getRepository.value) return false
    return ACCESS_TYPE_OWNER === getCurrentRepoUserAccessType.value
  })

  const isReadonly = computed(() => {
    if (!getRepository.value) return false
    return (
      getCurrentSettings.value?.readonly ||
      !getCurrentRepoUserAccessType.value ||
      ACCESS_TYPE_READONLY === getCurrentRepoUserAccessType.value
    )
  })

  const isReadWrite = computed(() => {
    if (!getRepository.value) return false
    return (
      !getCurrentSettings.value?.readonly &&
      ACCESS_TYPE_READ_WRITE === getCurrentRepoUserAccessType.value
    )
  })

  const getCurrentPlanSchema = computed(() => getRepository.value?.plan)

  const getSortedTabs = computed<string[]>(() => getCurrentUISettings.value?.sortedTabs || [])

  const getAvailableWidgets = computed(
    () => getCurrentPlanSchema.value?.dashboard_widgets || [],
  )

  const getAvailableTransactionTypes = computed(
    () => getCurrentPlanSchema.value?.transaction_types || [],
  )

  // SETTERS

  const setList = (data?: Repository[]) => {
    list.value = data || []
  }

  const setTreeSearch = (value: string) => {
    treeSearch.value = value
  }

  const setCurrentId = (value: string | undefined) => {
    if (value === userSettingsStore.getLastActiveRepository) return
    userSettingsStore.setLastActiveRepository(value)
  }

  const setCurrentTimeline = async (
    timeline: RepositoryTimeline | undefined,
    isEndChanged = false,
  ) => {
    if (timeline === undefined) return
    if (isEndChanged) {
      analyticsStore.setDate(undefined)
      internalTimelineEnd.value = timeline.end
    }
    await updateSettings(timeline)
  }

  const setCurrentUISettings = (data: [string | string[], any][]) => {
    if (!internalUISettings.value) {
      internalUISettings.value = {}
    }
    data.forEach(([key, value]) => {
      internalUISettings.value && set(internalUISettings.value, key, value)
    })
    onSettingsUpdate()
  }

  const onSettingsUpdate = useDebounceFn(() => {
    updateSettings({ ui_settings: internalUISettings.value })
  }, DEBOUNCE_DELAY)

  const defineCurrent = async () => {
    try {
      await mainStore.checkBlockers()
    } catch {
      return
    }

    if (
      getCurrentId.value &&
      list.value.find(item => item.id === getCurrentId.value)
    )
      return

    if (list.value.length > 0) {
      setCurrentId(list.value?.[0]?.id)
    }
  }

  const increaseColaboratorsCount = (repositoryId: string) => {
    const repository = list.value.find(item => item.id === repositoryId)
    repository &&
      updateStoreListItem<Repository>(list.value, {
        ...repository,
        collaborators_count: repository.collaborators_count + 1,
      })
  }

  const decreaseColaboratorsCount = (repositoryId: string) => {
    const repository = list.value.find(item => item.id === repositoryId)
    repository &&
      updateStoreListItem<Repository>(list.value, {
        ...repository,
        collaborators_count: repository.collaborators_count - 1,
      })
  }

  // ACTIONS

  const fetch = async () => {
    loading.value = true
    try {
      const { data } = await api.get<ApiListResponse<Repository[]>>(
        'repositories',
        { signal: abortController.signal },
      )
      initFlag.value = true
      // Sort repositories by name. Later will be implemented on backend.
      const list = sortBy(data?.data || [], 'name')
      setList(list)
    } catch (err) {
      const e = err as AxiosError
      if (e?.response?.status === HttpStatusCode.PaymentRequired) {
        initFlag.value = true
      }
      throw Error(prepareResponseError(err))
    } finally {
      loading.value = false
    }
  }

  const fetchCurrencies = async (search?: string, store = true, abortController?: AbortController) => {
    try {
      const result = await api.get<RepositoryCurrency[]>('currencies', {
        params: {
          search,
        },
        signal: abortController?.signal,
      })
      if (store) {
        currenciesList.value = result.data
      }
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const store = async (repository: Repository) => {
    try {
      const result = await api.post<
        Repository,
        AxiosResponse<Repository>,
        Repository
      >('repositories', {
        ...repository,
        user_repo_settings: {
          ...DEFAULT_TIMELINE,
          ui_settings: null,
        },
      })
      const id = result.data.id
      if (id) {
        await api.post(`repositories/${id}/initialize`)
      }
      addStoreListItem<Repository>(list.value, result.data, 'name')
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const update = async (repository: Partial<Repository>) => {
    if (!repository.user_repo_settings) return
    loadingAction.value = true
    try {
      const result = await api.put<
        Repository,
        AxiosResponse<Repository>,
        Partial<Repository>
      >(`repositories/${repository.id}`, repository)
      if (repository.currency !== getCurrentCurrency.value) {
        analyticsStore.markAsDeprecated()
      }
      updateStoreListItem<Repository>(list.value, result.data)
      if (result.data.currency_asset) {
        assetsStore.pushAssetToList(result.data.currency_asset)
      }
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const updateSettings = async (settings: Record<string, any>, repositoryId = getCurrentId.value) => {
    try {
      const repository = getRepository.value
      if (!repository) return
      let repositorySettings = getCurrentRepoUserSettings.value
      if (repositoryId !== getCurrentId.value) {
        repositorySettings = list.value.find(item => item.id === repositoryId)?.user_repo_settings
      }
      const data = merge(repositorySettings, settings)
      if (repositoryId === getCurrentId.value) {
        updateStoreListItem<Repository>(list.value, {
          ...repository,
          user_repo_settings: data,
        })
      }
      await api.put(
        `repositories/${repositoryId}/user_repo_settings`,
        data,
      )
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const destroy = async (id: string) => {
    try {
      await api.delete(`repositories/${id}`)
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const remove = (repositoryId: string) => {
    removeStoreListItem<Repository>(list.value, repositoryId)
  }

  const exportRepository = async (repositoryId: string): Promise<string> => {
    try {
      const result = await api.get<{ task_id: string }>(`repositories/${repositoryId}/export`)
      const taskId = result.data?.task_id
      if (!taskId) throw Error('Repository export task id is not available')
      return taskId
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const fetchExportData = async (taskId: string): Promise<string> => {
    try {
      const taskResult = await api.get<{ meta_data: Record<string, string> | null }>(`tasks/${taskId}`)
      const url = taskResult.data?.meta_data?.repo_data_url
      if (!url) throw Error('Repository export URL is not available')
      const json = await api.get(url)
      return JSON.stringify(json.data, null, 2)
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const sortTabs = async (data: string[]) => {
    const sortedTabs = [...new Set([...data, ...getSortedTabs.value])]
    setCurrentUISettings([['sortedTabs', sortedTabs]])
  }

  const clearCache = async (repositoryId: string) => {
    try {
      await api.delete(`cache/analytics/${repositoryId}`)
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const cancel = () => {
    abortController.abort()
    abortController = new AbortController()
  }

  const clear = () => {
    initFlag.value = false
    loading.value = true
    loadingAction.value = false
    setList([])
  }

  watch(getCurrentId, () => {
    setTreeSearch('')
    internalUISettings.value = getCurrentUISettings.value
  })

  watch(
    getCurrentId,
    async (value, oldValue) => {
      if (!value || value === oldValue) return
      if (getCurrentRepoUserSettings.value?.end_is_today) {
        internalTimelineEnd.value = getCurrentDate({})
      } else {
        internalTimelineEnd.value = getCurrentRepoUserSettings.value?.end || getCurrentDate({})
      }
      if (oldValue != null) {
        await mainStore.cancelFetch()
        await mainStore.clearState()
      }
      await mainStore.fetchCurrentRepositoryData()
    },
    { immediate: true },
  )

  const { stop } = watchPausable(
    () => ({ settings: getCurrentUISettings.value, userMigrated: userSettingsStore.alreadyMigrated }),
    async ({ settings, userMigrated }) => {
      if (!settings || !userMigrated) return
      stop()
      const newSettings = await runRepositorySettingsMigrations(settings)
      newSettings && setCurrentUISettings(newSettings)
    }, { immediate: true })

  return {
    initFlag,
    loading,
    loadingAction,

    fetch,
    fetchCurrencies,

    getList,
    getCurrencies,
    getTreeSearch,

    getRepository,
    getCurrentId,
    getCurrentName,
    getCurrentCurrency,
    getCurrentCurrencySymbol,
    getCurrentUISettings,
    getCurrentTimeline,
    getCurrentTimelineEnd,
    getCurrentTimelineFreq,
    getCurrentTimelinePeriods,
    getCurrentCollaboratorsCount,
    getCurrentOwner,
    getSortedTabs,
    getAvailableWidgets,
    getAvailableTransactionTypes,

    isOwner,
    isReadonly,
    isReadWrite,

    setTreeSearch,
    setCurrentId,
    setCurrentTimeline,
    setCurrentUISettings,

    defineCurrent,
    increaseColaboratorsCount,
    decreaseColaboratorsCount,

    store,
    update,
    destroy,
    remove,
    export: exportRepository,
    fetchExportData,
    updateSettings,
    sortTabs,
    clearCache,

    cancel,
    clear,
  }
})
